import Modal from '../components/modal';
import ModalTranscript from '../components/modal-transcript';
import { getVideoEmbedURL } from './video-url';

function escapeAttribute(value) {
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/**
 * Show a video modal with the specified video URL
 *
 * @param {string} videoUrl The video URL
 * @param {Object} options Accessibility and content options for the video modal
 */
export async function showVideoModal(videoUrl, options = {}) {
	if (!videoUrl) {
		return;
	}

	const normalizedOptions = typeof options === 'string' ? { title: options } : options;

	const {
		title = 'Video',
		captions = '',
		transcript = '',
		audioDescribedUrl = '',
		isSilent = false,
		restoreFocusElement = null
	} = normalizedOptions;

	const embedUrl = getVideoEmbedURL(videoUrl);
	const escapedTitle = escapeAttribute(title);
	const isDirectURL = embedUrl.toLowerCase().indexOf('.mp4') !== -1;
	const videoId = `modal-video-${Date.now()}`;
	const transcriptId = `${videoId}-transcript`;
	const hasCaptions = captions && !isSilent;
	const hasTranscript = isDirectURL && transcript;
	const needsFocusSentinel = !hasTranscript && !audioDescribedUrl;

	const templateMarkup = `
		<div class="modal__video-container">
			${
				isDirectURL
					? `
						<video id="${videoId}" class="modal__video-embed" src="${embedUrl}" controls autoplay tabindex="0">
							${hasCaptions ? `
								<track kind="captions" src="${captions}" srclang="en" label="Captions" default>
							` : ''}
						</video>
					`
					: `
						<iframe
							class="modal__video-embed"
							src="${embedUrl}"
							title="${escapedTitle}"
							width="640"
							height="360"
							frameborder="0"
							allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
							allowfullscreen
							tabindex="0"
						></iframe>
					`
			}

			${hasTranscript ? `
				<div class="modal__video-actions">
					<button
						class="modal__transcript-toggle js-modal-video-transcript-toggle"
						type="button"
						aria-expanded="false"
						aria-controls="${transcriptId}"
					>
						Video Transcript
					</button>
				</div>

				<div
					id="${transcriptId}"
					class="modal__video-transcript js-modal-video-transcript"
					data-transcript-src="${transcript}"
					data-video-id="${videoId}"
					aria-label="Video transcript"
					hidden
				>
					<p class="modal__video-transcript-status js-modal-video-transcript-status" role="status">Loading transcript…</p>
					<ol class="modal__video-transcript-list js-modal-video-transcript-list" aria-label="Timed video transcript" hidden></ol>
					<noscript>
						<a href="${transcript}">Download the timed transcript</a>
					</noscript>
				</div>
			` : ''}

			${isDirectURL && audioDescribedUrl ? `
				<a class="modal__audio-described-link" href="${audioDescribedUrl}" target="_blank" rel="noopener">
					Watch the audio-described version
				</a>
			` : ''}

			${needsFocusSentinel ? `
				<span class="modal__video-focus-sentinel" tabindex="0" aria-hidden="true"></span>
			` : ''}
		</div>
	`;

	await Modal.show(templateMarkup, {
		modalClass: hasTranscript ? 'modal modal-video modal-video--transcript' : 'modal modal-video',
		ariaLabel: `Video: ${escapedTitle}`,
		restoreFocusElement
	});

	document.querySelectorAll('.modal .js-modal-video-transcript').forEach((transcriptEl) => {
		new ModalTranscript(transcriptEl);
	});

	document.querySelectorAll('.modal .js-modal-video-transcript-toggle').forEach((button) => {
		button.addEventListener('click', () => {
			const transcriptPanel = document.getElementById(button.getAttribute('aria-controls'));

			if (!transcriptPanel) return;

			const isOpen = !transcriptPanel.hidden;

			transcriptPanel.hidden = isOpen;
			button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
		});
	});

	const modal = document.querySelector('.modal');
	const mediaEl = document.querySelector('.modal__video-embed');
	const focusSentinel = document.querySelector('.modal__video-focus-sentinel');

	const focusCloseButton = () => {
		const closeButton = document.querySelector('.modal__close');

		if (!closeButton) {
			return;
		}

		setTimeout(() => {
			closeButton.focus();
		}, 0);
	};

	if (focusSentinel) {
		focusSentinel.addEventListener('focus', focusCloseButton);
	}

	if (mediaEl && !isDirectURL && focusSentinel) {
		mediaEl.addEventListener('blur', () => {
			setTimeout(() => {
				const activeElement = document.activeElement;

				if (
					activeElement === document.body ||
					activeElement === focusSentinel ||
					!modal.contains(activeElement)
				) {
					focusCloseButton();
				}
			}, 0);
		});
	}

	const refreshVideoCaptions = () => {
		if (!isDirectURL || !mediaEl || !mediaEl.textTracks) return;

		Array.from(mediaEl.textTracks).forEach((track) => {
			if (track.mode !== 'showing') {
				return;
			}

			track.mode = 'hidden';

			requestAnimationFrame(() => {
				track.mode = 'showing';
			});
		});
	};

	if (isDirectURL && mediaEl) {
		let controlsHideTimer = null;

		const scheduleCaptionRefreshOnControlsHide = () => {
			clearTimeout(controlsHideTimer);
			controlsHideTimer = setTimeout(refreshVideoCaptions, 3000);
		};

		[
			'mouseenter',
			'focus',
			'blur',
			'play',
			'pause',
			'seeking',
			'seeked'
		].forEach((eventName) => {
			mediaEl.addEventListener(eventName, refreshVideoCaptions);
		});

		mediaEl.addEventListener('mousemove', scheduleCaptionRefreshOnControlsHide);

		mediaEl.addEventListener('mouseleave', () => {
			clearTimeout(controlsHideTimer);
			setTimeout(refreshVideoCaptions, 1000);
		});
	}

	if (mediaEl) {
		mediaEl.addEventListener(isDirectURL ? 'loadedmetadata' : 'load', () => {
			Modal.setLoading(false);
		});
	}
}